import { useContext, useEffect } from 'react';
import {
  onAnalyticsEnabled,
  onConsentQueueDiscarded,
  willSendOrQueue,
} from '@libertify.frontend/analytics';
import { AppContext } from '../contexts/AppContext';
import { MediaAnalyticsEvents } from '../types';
import { useAnalytics } from './useAnalytics';

// Module scope so a remounted viewer (mobile video/document swap) doesn't resend it
const impressionSentFor = new Set<string>();

// A reject dropped the queued impressions: resend them if the viewer accepts later
onConsentQueueDiscarded(() => impressionSentFor.clear());

/** Sends `document_impression` once per experience per page load, as soon as the document is `shown`. */
export function useDocumentImpression(shown: boolean) {
  const { trackEvent } = useAnalytics();
  const { identifiers } = useContext(AppContext);
  const experienceKey = identifiers.publicationId || identifiers.projectId || '';

  useEffect(() => {
    if (
      !shown ||
      impressionSentFor.has(experienceKey) ||
      !willSendOrQueue()
    )
      return;
    impressionSentFor.add(experienceKey);
    trackEvent(MediaAnalyticsEvents.DocumentImpression);
  }, [shown, experienceKey, trackEvent]);

  // Resend the impression dropped before the viewer accepted cookies
  useEffect(() => {
    if (!shown) return undefined;
    return onAnalyticsEnabled(() => {
      if (impressionSentFor.has(experienceKey)) return;
      impressionSentFor.add(experienceKey);
      trackEvent(MediaAnalyticsEvents.DocumentImpression);
    });
  }, [shown, experienceKey, trackEvent]);
}
